import { types, flow, getEnv, Instance, SnapshotOut } from 'mobx-state-tree';
import { errorIsInApiRequest, errorIsCancel, IGroupedApiCtx, LiveEvents, ProgressStateModelForStore, CancelError, ProgressStateModel, SportsBettingApiResult, DesktopLiveEventListStore } from 'eTip--common';
import { ISportsBettingLiveApi } from '../../api/live/api';
import { isNumber } from 'easit--utils';
import { IAppEnv } from '../../appEnvironment';

export const LiveEventInPrematchListItemStore = types
    .model("LiveEventInPrematchListItemStore", {
        ggId: types.maybeNull(types.string), // it may be null if not odds
        eventId: types.string, // for map of events
        rowId: types.number // to be assignable to (game)detailId
    });

/**
 * Store for live events list.
 * For MTS Sequence in Event is compound of Region/Tournament (path is shown in list for the first one).
 */
const LiveEventInPrematchListKey = 'liveEventInPrematchList';
export const LiveEventInPrematchListStore = types
    .model(LiveEventInPrematchListKey, {
        events: types.map(DesktopLiveEventListStore),
        isDetailShown: types.optional(types.boolean, false),
        isDetailShownAsDefault: types.optional(types.boolean, true), //in list, use did no action to open a detail, use with isDetailShown
        lastReloadTime: types.maybeNull(types.number),
        state: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        forceRerenderNumber: types.optional(types.number, 0),
        partsData: types.optional(types.map(types.array(LiveEventInPrematchListItemStore)), {}) // list for each category + game
    })
    .views(self => {

        const env = getEnv<IAppEnv>(self);
        return {
            get refreshTime() {
                return env.settings.liveEventListRefreshTimeMs;
            }
        }
    })
    .actions(self => {
        const env = getEnv<IAppEnv>(self);

        let synchroCtx: IGroupedApiCtx<ISportsBettingLiveApi> | null = null;

        /**
         */
        const initAsync: (operationInfo: { wasEventAdded: boolean }) => Promise<void> = flow(function* (operationInfo: { wasEventAdded: boolean }) {

            self.lastReloadTime = 0;
            self.state = ProgressStateModel.Loading;

            self.events.clear();
            self.partsData.clear();
            _groupCats = null;

            yield loadDataCommonAsync(true, operationInfo);
        });

        let _groupCats: { gameId: string, sportId: string, regionId: string, competititonId: string, origCatIds: Set<string> | null, specificLiveEventsIds: Set<number> | null }[];
        const setGroupCats = (groupCats: { gameId: string, sportId: string, regionId: string, competititonId: string, origCatIds: Set<string> | null, specificLiveEventsIds: Set<number> | null }[]): void => {
            if (_groupCats && groupCats && _groupCats.length == groupCats.length) {
                // compare if it's same. If yes, do not call preparePartsData. But it happens only if there is more than CategoryEventsStore.loadingBatchCount (50) events in one game and competition (item). preparePartsData called for every update from api thus this test is probably overkill.
                if (_groupCats.every((item, index) => {
                    let destItem = groupCats[index];
                    return item.gameId === destItem.gameId
                        && item.sportId === destItem.sportId
                        && item.regionId === destItem.regionId
                        && item.competititonId == destItem.competititonId
                        && JSON.stringify(item.specificLiveEventsIds) === JSON.stringify(destItem.specificLiveEventsIds);
                })) {
                    return; // it's same
                }
            }
            _groupCats = groupCats;
            preparePartsData();
        };

        const preparePartsData = () => {
            if (!_groupCats) {
                return;
            }
            let touchedKeys = new Set<string>();
            _groupCats.forEach(gc => {
                let key = getPartDataKey(gc.gameId, gc.sportId, gc.regionId, gc.competititonId);
                let data = self.partsData.get(key);
                if (!data) {
                    self.partsData.set(key, []);
                    data = self.partsData.get(key);
                } else {
                    data.clear();
                }
                touchedKeys.add(key);
                let source = getEventsToRender(gc.competititonId, getRawGameId(gc.gameId), gc.origCatIds, gc.specificLiveEventsIds);
                if (source) {
                    source.forEach(row => {
                        let destRow = LiveEventInPrematchListItemStore.create(row, env);
                        data.push(destRow);
                    });
                }
            });
            let toRemove: string[] = [];
            for (let key of self.partsData.keys()) {
                if (!touchedKeys.has(key)) {
                    toRemove.push(key);
                }
            }
            toRemove.forEach(item => self.partsData.delete(item));
        };

        /**
         * Refresh data.
         *
         * Possible CancelError in reject.
         */
        const reloadDataAsync: (operationInfo: { wasEventAdded: boolean }) => Promise<void> = flow(function* (operationInfo: { wasEventAdded: boolean }) {
            yield loadDataCommonAsync(false, operationInfo);
        });

        /**
         * Loads data (both for full or refresh, depends on self.state).
         *
         * Reject may return CancelError.
         */

        const loadDataCommonAsync: (asFullLoad: boolean, result: { wasEventAdded: boolean }) => Promise<void> = flow(function* (asFullLoad: boolean, operationInfo: { wasEventAdded: boolean }) {
            let ctx: IGroupedApiCtx<ISportsBettingLiveApi> | null = null;
            try {
                //env.loggingService.info("LiveEventList start " + self.loadDataId + " " + self.state);
                if (synchroCtx && synchroCtx.isOpen()) {
                    if (!asFullLoad) {
                        throw new CancelError(); //does nothing, already waiting for response
                    }
                    //env.loggingService.info("LiveEventList cancelled previous in " + self.loadDataId);
                    synchroCtx.cancel('Load has been canceled.');
                    synchroCtx.close();
                    synchroCtx = null;
                }
                if (asFullLoad) {
                    self.state = ProgressStateModel.Loading;
                }

                ctx = env.liveApi.open();
                synchroCtx = ctx;
                const response: LiveEvents.RootObject = yield ctx.api.GetLiveEventsInPrematchDesktopAsync(
                    asFullLoad ? 0 : self.lastReloadTime
                );

                if (synchroCtx !== ctx) {
                    throw new CancelError();
                }

                if (response.Result == SportsBettingApiResult.Success) { //also NoChange may come
                    self.lastReloadTime = response.TimeStamp;
                    setData(response.ReturnValue, operationInfo);
                    preparePartsData();
                }

                self.state = ProgressStateModel.Success;
                //env.loggingService.info("LiveEventList success " + self.loadDataId);
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error(error);
                if (asFullLoad || !errorIsInApiRequest(error)) {
                    self.state = ProgressStateModel.Failed;
                }
                throw error;
            } finally {
                if (ctx === synchroCtx) {
                    synchroCtx?.close();
                    synchroCtx = null;
                }
            }
        });

        const setLoaded = (): void => {
            self.state = ProgressStateModel.Success;
        }

        const setData = (items: LiveEvents.ReturnValueItem[], operationInfo: { wasEventAdded: boolean }): void => {
            let touchedEventIds = new Set<string>();
            items?.forEach(sourceCat => {
                sourceCat.Events.forEach(sourceEvt => {
                    let evtId = sourceEvt.ID.toString();
                    touchedEventIds.add(evtId);
                    let dest = self.events.get(evtId);
                    if (dest) {
                        dest.setData(sourceEvt, sourceCat.GraphicsPresentationID);
                        return;
                    }
                    dest = DesktopLiveEventListStore.create(<SnapshotOut<typeof DesktopLiveEventListStore>>{
                        sequence: -1,
                        externalId: sourceEvt.ExternalID,
                        forceCanConvertTipName: true
                    }, env);
                    dest.setData(sourceEvt, sourceCat.GraphicsPresentationID);
                    self.events.set(evtId, dest);
                    operationInfo.wasEventAdded = true;
                });
            });
            let toRemove: string[] = [];
            for (let id of self.events.keys()) {
                if (!touchedEventIds.has(id)) {
                    toRemove.push(id);
                }
            }
            toRemove.forEach(id => self.events.delete(id));
        };

        const getEventsToRender = (competitionId: string | null, rawGameId: string | null, origCatIds: Set<string> | null, specificLiveEventsIds: Set<number> | null) => {
            let result: ILiveEventInPrematchListItemStore[] = [];

            for (let evt of self.events.values()) {
                let doCatTest = true;
                if (specificLiveEventsIds) {
                    if (specificLiveEventsIds.has(evt.id)) {
                        doCatTest = false;
                    } else {
                        continue;
                    }
                }
                if (doCatTest) {
                    let catId = evt.catIds[1];
                    let destCatId = `xx${catId}`;
                    if (destCatId !== competitionId && (!origCatIds || !origCatIds.has(destCatId))) {
                        continue;
                    }
                }
                let ggToShow: string = undefined;
                var foundIndex = evt.gameGroups.findIndex(gg => !!gg.games.find(g => g.id.startsWith(`${rawGameId}d`)));
                if (foundIndex >= 0) {
                    ggToShow = evt.gameGroups[foundIndex].id;
                }
                result.push({ ggId: ggToShow, eventId: evt.id.toString(), rowId: rawGameId ? Number(rawGameId) : -1 /* what else? does occur? */ });
            }
            return result;
        }

        const getRawGameId = (gameId: string): string | null => {
            if (isNumber(gameId)) {
                return gameId;
            }

            if (gameId.startsWith("gd")) /* /* see createGameDetailRowId */ {
                return gameId.substring(2);
            }

            return null;
        }

        const incForceRerenderNumber = () => {
            self.forceRerenderNumber++;
        }

        const getPartDataKey = (gameId: string, sportId: string, regionId: string, competitionId: string): string => {
            return `${gameId}__${sportId}__${regionId}__${competitionId}`;
        };

        const clear = (): void => {
            setGroupCats([]);
            self.events.clear();
            self.state = ProgressStateModel.Loading;
            if (synchroCtx && synchroCtx.isOpen()) {
                synchroCtx.cancel();
                synchroCtx = null;
            }
        }

        return {
            initAsync,
            reloadDataAsync,
            /*showDetail,
            hideDetail,
            toggleDetail,*/
            getRawGameId,
            incForceRerenderNumber,
            getPartDataKey,
            setGroupCats,
            clear,
            setLoaded
        };
    });

export type ILiveEventInPrematchListStore = Instance<typeof LiveEventInPrematchListKey>;

export type ILiveEventInPrematchListItemStore = Instance<typeof LiveEventInPrematchListItemStore>;